import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/EditOutlined";
import { useEffect, useMemo, useState } from "react";
import {
  DAYS_OF_WEEK,
  type DayOfWeek,
  type PremiseConfig,
  type ScheduleEntry,
} from "@shared/types/foyer";
import { activeScheduleFor, newScheduleId } from "@shared/schedule";
import { PageHeader } from "../components/PageHeader";

type RuleKind = "weekly" | "date-range" | "single";

const DAY_LABEL: Record<DayOfWeek, string> = {
  mon: "Mon",
  tue: "Tue",
  wed: "Wed",
  thu: "Thu",
  fri: "Fri",
  sat: "Sat",
  sun: "Sun",
};

interface Props {
  premise: PremiseConfig;
  onChange: (next: PremiseConfig) => void;
}

/**
 * Per-premise scheduling rules - what page the TV considers "main" at
 * a given time/date. The TV evaluates rules locally every minute.
 *
 * All mutations land in the parent draft (which the editor's top-bar
 * Save persists via the full premise PUT). No separate API call.
 */
export default function SchedulePage({ premise, onChange }: Props) {
  const schedules = useMemo(() => premise.schedules ?? [], [premise.schedules]);
  const [editing, setEditing] = useState<ScheduleEntry | null>(null);
  const [creating, setCreating] = useState(false);

  // Re-render every minute so the "Active now" badge tracks reality.
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = window.setInterval(() => setTick((n) => n + 1), 60_000);
    return () => window.clearInterval(t);
  }, []);

  const activeAll = useMemo(
    () =>
      activeScheduleFor(schedules, new Date(), null, premise.address?.timezone),
    [schedules, premise.address?.timezone]
  );

  function upsert(next: ScheduleEntry) {
    const list = schedules.some((s) => s.id === next.id)
      ? schedules.map((s) => (s.id === next.id ? next : s))
      : [...schedules, next];
    onChange({ ...premise, schedules: list });
  }

  function remove(id: string) {
    onChange({
      ...premise,
      schedules: schedules.filter((s) => s.id !== id),
    });
  }

  function toggleEnabled(s: ScheduleEntry, enabled: boolean) {
    upsert({ ...s, enabled });
  }

  const pageById = new Map(premise.pages.map((p) => [p.id, p.name]));

  return (
    <Stack spacing={3} data-testid="schedule-page">
      <PageHeader
        eyebrow="TV"
        title="Schedule"
        help="Rules that override which page the TV shows based on time and date. The highest-priority rule that matches wins; when nothing matches, the page flagged as Main is used. Times are 24h, local to the premise timezone."
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreating(true)}
            data-testid="schedule-new"
            sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
          >
            New rule
          </Button>
        }
      />

      <Paper variant="outlined">
        <Table size="small" data-testid="schedule-list">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Page</TableCell>
              <TableCell>When</TableCell>
              <TableCell>Scope</TableCell>
              <TableCell align="right">Priority</TableCell>
              <TableCell align="center">Enabled</TableCell>
              <TableCell sx={{ width: 96 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {schedules.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="caption" color="text.secondary">
                    No rules yet. Add one to override the TV&apos;s default main
                    page during specific times or dates.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {schedules.map((s) => {
              const isActive = activeAll?.id === s.id;
              return (
                <TableRow key={s.id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {s.name || "(unnamed)"}
                      </Typography>
                      {isActive && (
                        <Chip size="small" color="success" label="Active now" />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {pageById.get(s.pageId) ?? (
                        <span style={{ color: "rgba(0,0,0,0.4)" }}>
                          (deleted page)
                        </span>
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {summariseWindow(s)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {s.scopeAllRooms
                        ? "All rooms"
                        : `${s.roomIds?.length ?? 0} room${
                            s.roomIds?.length === 1 ? "" : "s"
                          }`}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{s.priority}</TableCell>
                  <TableCell align="center">
                    <Switch
                      size="small"
                      checked={s.enabled}
                      onChange={(e) => toggleEnabled(s, e.target.checked)}
                      inputProps={
                        {
                          "data-testid": `schedule-toggle-${s.id}`,
                        } as React.InputHTMLAttributes<HTMLInputElement>
                      }
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    <Tooltip title="Edit rule">
                      <IconButton
                        size="small"
                        aria-label="Edit schedule rule"
                        onClick={() => setEditing(s)}
                        data-testid={`schedule-edit-${s.id}`}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete rule">
                      <IconButton
                        size="small"
                        aria-label="Delete schedule rule"
                        onClick={() => remove(s.id)}
                        data-testid={`schedule-delete-${s.id}`}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      <ScheduleEditorDialog
        open={creating || !!editing}
        initial={editing}
        premise={premise}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        onSubmit={(entry) => {
          upsert(entry);
          setCreating(false);
          setEditing(null);
        }}
      />
    </Stack>
  );
}

function summariseWindow(s: ScheduleEntry): string {
  const parts: string[] = [];
  if (s.startDate || s.endDate) {
    if (s.startDate && s.endDate && s.startDate === s.endDate) {
      parts.push(s.startDate);
    } else {
      parts.push(`${s.startDate ?? "-"} → ${s.endDate ?? "-"}`);
    }
  }
  if (s.daysOfWeek && s.daysOfWeek.length > 0 && s.daysOfWeek.length < 7) {
    parts.push(s.daysOfWeek.map((d) => DAY_LABEL[d]).join(", "));
  } else if (!s.startDate && !s.endDate) {
    parts.push("every day");
  }
  if (s.startTime || s.endTime) {
    parts.push(`${s.startTime ?? "00:00"}-${s.endTime ?? "24:00"}`);
  } else {
    parts.push("all day");
  }
  return parts.join(" · ");
}

interface DialogProps {
  open: boolean;
  initial: ScheduleEntry | null;
  premise: PremiseConfig;
  onClose: () => void;
  onSubmit: (entry: ScheduleEntry) => void;
}

function inferKind(s: ScheduleEntry | null): RuleKind {
  if (!s) return "weekly";
  if (s.startDate && s.endDate && s.startDate === s.endDate) return "single";
  if (s.startDate || s.endDate) return "date-range";
  return "weekly";
}

function ScheduleEditorDialog({
  open,
  initial,
  premise,
  onClose,
  onSubmit,
}: DialogProps) {
  const [name, setName] = useState("");
  const [pageId, setPageId] = useState("");
  const [kind, setKind] = useState<RuleKind>("weekly");
  const [days, setDays] = useState<DayOfWeek[]>([]);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [scopeAll, setScopeAll] = useState(true);
  const [roomIds, setRoomIds] = useState<string[]>([]);
  const [priority, setPriority] = useState(0);
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setName(initial.name);
      setPageId(initial.pageId);
      setKind(inferKind(initial));
      setDays(initial.daysOfWeek ?? []);
      setStartTime(initial.startTime ?? "");
      setEndTime(initial.endTime ?? "");
      setStartDate(initial.startDate ?? "");
      setEndDate(initial.endDate ?? "");
      setScopeAll(initial.scopeAllRooms);
      setRoomIds(initial.roomIds ?? []);
      setPriority(initial.priority ?? 0);
      setEnabled(initial.enabled);
    } else {
      setName("");
      setPageId(
        premise.pages.find((p) => p.isMain)?.id ?? premise.pages[0]?.id ?? ""
      );
      setKind("weekly");
      setDays([]);
      setStartTime("");
      setEndTime("");
      setStartDate("");
      setEndDate("");
      setScopeAll(true);
      setRoomIds([]);
      setPriority(0);
      setEnabled(true);
    }
  }, [open, initial, premise.pages]);

  function toggleDay(d: DayOfWeek) {
    setDays((cur) =>
      cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d]
    );
  }

  function submit() {
    const entry: ScheduleEntry = {
      id: initial?.id ?? newScheduleId(),
      name: name.trim() || "Untitled rule",
      pageId,
      scopeAllRooms: scopeAll,
      roomIds: scopeAll ? undefined : roomIds,
      daysOfWeek:
        kind === "weekly" && days.length > 0 && days.length < 7
          ? days
          : undefined,
      startTime: startTime || undefined,
      endTime: endTime || undefined,
      startDate:
        kind === "single"
          ? startDate || undefined
          : kind === "date-range"
            ? startDate || undefined
            : undefined,
      endDate:
        kind === "single"
          ? startDate || undefined
          : kind === "date-range"
            ? endDate || undefined
            : undefined,
      priority,
      enabled,
    };
    onSubmit(entry);
  }

  const canSave =
    !!pageId &&
    (scopeAll || roomIds.length > 0) &&
    (kind !== "single" || !!startDate) &&
    (kind !== "date-range" || (!!startDate && !!endDate));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initial ? "Edit rule" : "New schedule rule"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            size="small"
            autoFocus
            inputProps={{ "data-testid": "sched-name" }}
          />
          <TextField
            select
            label="Show page"
            value={pageId}
            onChange={(e) => setPageId(e.target.value)}
            size="small"
            inputProps={{ "data-testid": "sched-page" }}
          >
            {premise.pages.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
                {p.isMain ? " (Main)" : ""}
              </MenuItem>
            ))}
          </TextField>

          <Tabs
            value={kind}
            onChange={(_e, v) => setKind(v as RuleKind)}
            variant="fullWidth"
          >
            <Tab label="Weekly" value="weekly" />
            <Tab label="Date range" value="date-range" />
            <Tab label="Single date" value="single" />
          </Tabs>

          {kind === "weekly" && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Days (leave all unchecked for every day)
              </Typography>
              <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                {DAYS_OF_WEEK.map((d) => (
                  <Chip
                    key={d}
                    label={DAY_LABEL[d]}
                    onClick={() => toggleDay(d)}
                    color={days.includes(d) ? "primary" : "default"}
                    variant={days.includes(d) ? "filled" : "outlined"}
                    size="small"
                    data-testid={`sched-day-${d}`}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {kind === "date-range" && (
            <Stack direction="row" spacing={2}>
              <TextField
                type="date"
                label="Start"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={{ "data-testid": "sched-start-date" }}
              />
              <TextField
                type="date"
                label="End"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={{ "data-testid": "sched-end-date" }}
              />
            </Stack>
          )}

          {kind === "single" && (
            <TextField
              type="date"
              label="Date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={{ "data-testid": "sched-single-date" }}
            />
          )}

          <Stack direction="row" spacing={2}>
            <TextField
              label="From (HH:mm)"
              placeholder="HH:mm"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              size="small"
              helperText="Blank = start of day"
              inputProps={{
                pattern: "([01][0-9]|2[0-3]):[0-5][0-9]",
                maxLength: 5,
                "data-testid": "sched-start-time",
              }}
            />
            <TextField
              label="To (HH:mm)"
              placeholder="HH:mm"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              size="small"
              helperText="Blank = end of day; end is exclusive"
              inputProps={{
                pattern: "([01][0-9]|2[0-3]):[0-5][0-9]",
                maxLength: 5,
                "data-testid": "sched-end-time",
              }}
            />
          </Stack>

          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={scopeAll}
                  onChange={(e) => setScopeAll(e.target.checked)}
                  inputProps={
                    {
                      "data-testid": "sched-scope-all",
                    } as React.InputHTMLAttributes<HTMLInputElement>
                  }
                />
              }
              label="Apply to every room"
            />
            {!scopeAll && (
              <TextField
                select
                fullWidth
                size="small"
                label="Rooms"
                value={roomIds}
                onChange={(e) =>
                  setRoomIds(
                    typeof e.target.value === "string"
                      ? e.target.value.split(",")
                      : (e.target.value as string[])
                  )
                }
                SelectProps={{
                  multiple: true,
                  renderValue: (v) => {
                    const ids = v as string[];
                    return ids.length === 0 ? "(none)" : ids.join(", ");
                  },
                }}
                helperText={`${roomIds.length} of ${premise.rooms.length} room${
                  premise.rooms.length === 1 ? "" : "s"
                } selected`}
                inputProps={{ "data-testid": "sched-rooms" }}
              >
                {premise.rooms.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.id}
                    {r.name ? ` - ${r.name}` : ""}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Box>

          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              type="number"
              label="Priority"
              value={priority}
              onChange={(e) => setPriority(Number(e.target.value) || 0)}
              size="small"
              helperText="Higher wins on conflict"
              sx={{ width: 180 }}
              inputProps={{ "data-testid": "sched-priority" }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                />
              }
              label="Enabled"
            />
          </Stack>

          {!canSave && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Pick a page
              {kind === "single" && !startDate ? " and a date" : ""}
              {kind === "date-range" && (!startDate || !endDate)
                ? " and a start + end date"
                : ""}
              {!scopeAll && roomIds.length === 0
                ? " and at least one room"
                : ""}{" "}
              to save.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!canSave}
          onClick={submit}
          data-testid="sched-save"
        >
          {initial ? "Save" : "Add rule"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
